import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, forwardRef } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';

import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, map, Observable, of, Subscription, switchMap, take } from 'rxjs';

import { SelectItem } from 'primeng/api';

import { DateTime } from 'luxon';

import { getSiteInfo } from '@shared/helpers/site-info';
import { SitesService } from '@shared/services/sites.service';
import { WorkedShiftsService } from '@shared/services/worked-shifts.service';
import { ShiftContextService } from '../../services/shift-context.service';

import { Shift } from '@shared/models/shift';
import { ShiftStatus, WorkedShift } from '@shared/models/worked-shift';
import { ShiftContext } from '../../models/shift-context';
import { fromFormControl } from '@shared/helpers/from-form-control';
import { DateInfo } from '@shared/components/worked-calendar/worked-calendar.component';

@Component({
    selector: 'app-worked-shift-selector',
    templateUrl: './worked-shift-selector.component.html',
    styleUrls: ['./worked-shift-selector.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => WorkedShiftSelectorComponent),
        }
    ]
})
export class WorkedShiftSelectorComponent implements OnInit, OnDestroy, ControlValueAccessor {
    ShiftStatus = ShiftStatus;

    @Input() set value(val: number) {
        this.workedShiftId.next(val);
    }

    @Input() inputId: string;
    @Input() readonly: boolean;
    @Input() name: string;
    @Input() tabindex: number;
    @Input() showClearButton: boolean = false;

    @Input() set disabled(val: boolean) {
        if (val)
            this.form.disable();
        else
            this.form.enable();
    }

    @Output() input = new EventEmitter<number>();
    @Output() dateSelected = new EventEmitter<DateTime>();
    @Output() shiftSelected = new EventEmitter<Shift>();
    @Output() workedShiftSelected = new EventEmitter<WorkedShift>();

    currentContext = new BehaviorSubject<ShiftContext | null>(null);

    workedShifts = new BehaviorSubject<WorkedShift[]>([]);

    shifts = this.currentContext.pipe(switchMap(context => context ? this.sitesService.GetShiftsForSite(context.site.id) : of([])));

    shiftItems: Observable<SelectItem<number>[]> = combineLatest([
        this.shifts,
        this.workedShifts
    ]).pipe(map(([shifts, workedShifts]) => shifts
        ?.filter(s => workedShifts?.some(ws => ws.shiftId === s.id) ?? false)
        .map(s => ({ value: s.id, label: s.name }))
        ?? []));

    form = new FormGroup({
        date: new FormControl<Date>(null),
        shiftId: new FormControl<number>(null)
    });

    get date(): DateTime {
        const date = this.form.controls.date.value;
        return date ? DateTime.fromJSDate(date) : null;
    }
    set date(val: DateTime) {
        this.form.controls.date.setValue(val?.toJSDate() ?? null);
    }

    workedShiftId = new BehaviorSubject<number | null>(null);

    selectedWorkedShift = this.workedShiftId.pipe(
        filter(id => !!id),
        distinctUntilChanged(),
        switchMap(id => this.workedShiftsService.FollowById(id)));

    isTpm = this.currentContext.pipe(map(c => c ? getSiteInfo(c.site).isTpm : false));

    private subscriptions: Subscription[] = [];

    private onChange: ((val: number) => void)[] = [];
    private onTouched: (() => void)[] = [];

    constructor(
        private shiftContextService: ShiftContextService,
        private sitesService: SitesService,
        private workedShiftsService: WorkedShiftsService) { }

    ngOnInit(): void {
        this.subscriptions = [
            this.workedShiftId.subscribe(id => {
                this.onChange.forEach(f => f(id));
            }),

            this.shiftContextService.CurrentContext.subscribe(context => {
                this.currentContext.next(context);
            }),

            this.selectedWorkedShift.subscribe(ws => {
                this.form.setValue({
                    date: ws?.date?.toJSDate() ?? null,
                    shiftId: ws?.shiftId ?? null
                })

                this.input.emit(ws?.id ?? null);
                this.workedShiftSelected.emit(ws);
            }),

            combineLatest([
                fromFormControl(this.form.controls.shiftId),
                this.shifts
            ]).subscribe(([shiftId, shifts]) => {
                this.shiftSelected.emit(shifts?.find(s => s.id === shiftId) ?? null);
            }),

            combineLatest([
                this.currentContext,
                fromFormControl(this.form.controls.date)
            ]).pipe(
                filter(([context, date]) => !!context && !!date),
                switchMap(([context, date]) => this.workedShiftsService.FollowByDate(context.site.id, DateTime.fromJSDate(date)))
            ).subscribe(updatedWorkedShifts => {
                this.workedShifts.next(updatedWorkedShifts);

                if (this.form.controls.shiftId.value !== null) {
                    this.onShiftSelected(this.form.controls.shiftId.value);
                }
            })
        ];
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    setNow(): void {
        this.workedShiftId.next(this.currentContext.value.id);
    }

    touched() {
        this.onTouched.forEach(f => f());
    }

    registerOnTouched(fn: any): void {
        this.onTouched.push(fn);
    }

    registerOnChange(fn: any): void {
        this.onChange.push(fn);
    }

    writeValue(obj: any): void {
        this.workedShiftId.next(obj);
    }

    clearSelection() {
        this.workedShiftId.next(null);
    }

    onDateSelected(date: DateInfo | DateInfo[]) {
        if (Array.isArray(date))
            date = date[0];

        this.dateSelected.emit(date.date);
    }

    onShiftSelected(shiftId: number) {
        const workedShift = this.workedShifts.value.find(
            ws => ws.date.equals(this.date) && ws.shiftId === shiftId
        );

        if(workedShift === undefined) {
            return;
        }

        this.workedShiftId.next(workedShift.id);
    }
}
